import React, { useEffect, useState } from 'react';
import { Heart, Calendar, Images, Image as ImageIcon, Pin } from 'lucide-react';
import type { WorkUploadState } from '../../services/postService';

interface VideoCardProps {
    id: string | number;
    type?: 'video' | 'image';
    videoUrl?: string;
    images?: string[];
    thumbnailUrl: string;
    title: string;
    likes: number;
    createdAt: string;
    auditStatus?: 'PENDING' | 'REJECTED' | 'APPROVED';
    visibility?: 'public' | 'private' | 'unlisted' | 'draft';
    uploadState?: WorkUploadState;
    uploadProgress?: number;
    liked?: boolean;
    isPinned?: boolean;
    onClick?: () => void;
    onAppeal?: () => void;
    appealStatus?: 'pending' | 'approved' | 'rejected' | null;
}

const VideoCard: React.FC<VideoCardProps> = ({
    type = 'video',
    images,
    thumbnailUrl,
    title,
    likes,
    createdAt,
    auditStatus = 'APPROVED',
    visibility = 'public',
    uploadState,
    liked = false,
    isPinned = false,
    onClick,
    onAppeal,
    appealStatus
}) => {
    const imageCount = images?.length || 0;
    const [hasThumbnailError, setHasThumbnailError] = useState(false);
    const isDraft = visibility === 'draft';
    const isHidden = visibility === 'private';
    const isUploading = !!uploadState;

    useEffect(() => {
        setHasThumbnailError(false);
    }, [thumbnailUrl]);

    const handleCardClick = () => {
        onClick?.();
    };

    return (
        <div
            className={`group relative rounded-2xl overflow-hidden bg-gray-100 transition-transform duration-300 ${isUploading ? 'cursor-pointer opacity-80 hover:shadow-xl' : auditStatus === 'REJECTED' || isHidden ? 'cursor-pointer hover:shadow-xl' : 'cursor-pointer hover:scale-[1.02] hover:shadow-xl'
                }`}
            onClick={handleCardClick}
        >
            <div className="aspect-[4/3] relative">
                {/* 封面图 */}
                {!thumbnailUrl || hasThumbnailError ? (
                    <div className="w-full h-full bg-gradient-to-br from-slate-200 via-slate-100 to-slate-300 transition-transform duration-500 group-hover:scale-105" />
                ) : (
                    <img
                        src={thumbnailUrl}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={() => setHasThumbnailError(true)}
                    />
                )}

                {/* 类型指示图标 */}
                <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
                    <div className="w-8 h-8 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center">
                        {type === 'video' ? (
                            <svg className="w-3.5 h-3.5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        ) : imageCount > 1 ? (
                            <Images className="w-4 h-4 text-white" />
                        ) : (
                            <ImageIcon className="w-4 h-4 text-white" />
                        )}
                    </div>
                    {isDraft && (
                        <div className="rounded-full bg-gray-100/95 px-3 py-1 text-[11px] font-semibold text-gray-500 shadow-sm">
                            草稿
                        </div>
                    )}
                    {isHidden && (
                        <div className="rounded-full bg-gray-100/95 px-3 py-1 text-[11px] font-semibold text-gray-500 shadow-sm">
                            隐藏
                        </div>
                    )}
                    {isUploading ? (
                        <div className="rounded-full bg-black/65 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-sm shadow-sm">
                            {uploadState === 'processing' ? '处理中' : '上传中'}
                        </div>
                    ) : !isDraft && auditStatus === 'PENDING' && (
                        <div className="rounded-full bg-black/65 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-sm shadow-sm">
                            审核中
                        </div>
                    )}
                </div>
                {isPinned && (
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-full bg-blue-500/90 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                        <Pin className="h-3 w-3 fill-current" />
                        <span>置顶</span>
                    </div>
                )}

                {/* 底部信息遮罩 */}
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                    {/* 标题 */}
                    <h3 className="font-medium text-white text-sm mb-2 line-clamp-1 drop-shadow-lg">
                        {title}
                    </h3>

                    {/* 日期和点赞 */}
                    <div className="flex items-center justify-between text-white/80 text-xs">
                        <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{createdAt}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Heart className={`w-3 h-3 ${liked ? 'fill-red-500 text-red-500' : 'fill-white/80'}`} />
                            <span>{likes >= 1000 ? `${(likes / 1000).toFixed(1)}k` : likes}</span>
                        </div>
                    </div>
                </div>

                {/* Hover 遮罩效果 */}
                {!isUploading && auditStatus !== 'REJECTED' && (
                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                )}

                {isHidden && (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-gray-200/55 backdrop-grayscale">
                        <div className="-rotate-12 rounded-full border border-gray-400/60 bg-gray-100/85 px-5 py-2 text-sm font-semibold text-gray-600 shadow-sm">
                            已隐藏
                        </div>
                    </div>
                )}

                {/* Content Moderation Overlays */}
                {!isUploading && !isDraft && auditStatus === 'REJECTED' && (
                    <div className="absolute inset-0 bg-red-900/40 backdrop-blur-sm flex flex-col items-center justify-center z-10 gap-2">
                        <div className="bg-red-500 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg">
                            违规拦截
                        </div>
                        {appealStatus === 'pending' ? (
                            <div className="bg-yellow-100 text-yellow-700 text-xs font-medium px-3 py-1.5 rounded-full shadow">
                                申诉审核中
                            </div>
                        ) : appealStatus === 'rejected' ? (
                            <div className="bg-gray-100 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full shadow">
                                申诉未通过
                            </div>
                        ) : onAppeal && !appealStatus ? (
                            <button
                                onClick={(e) => { e.stopPropagation(); onAppeal(); }}
                                className="bg-white/90 text-gray-800 text-xs font-medium px-3 py-1.5 rounded-full shadow hover:bg-white transition-colors"
                            >
                                申诉
                            </button>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoCard;
